/**
 * ViewMenu.jsx — View top-level menu.
 * Panel visibility checkboxes, Reset workspace, Focus mode.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Menu, MenuItem, useIsGuidanceMode } from './shared';

const PANEL_TIPS = {
  home:      'Your story hub — see all projects and quick-start tasks',
  editor:    'The main prose-writing editor for the current chapter',
  chapters:  'Jump between, reorder, or rename your chapters',
  modes:     'Switch between Draft, Revise, Line Edit, and other writing modes',
  variables: 'Track names, places, dates, and facts that must stay consistent',
  bible:     'View and edit your full cast of characters and their relationships',
  arc:       'See a graph of your story\'s emotional and narrative arc',
  chat:      'Have a real-time conversation with the AI about your story',
  analysis:  'Run deep AI analysis on chapters or the whole novel',
  marketing: 'See reader demand data and marketing opportunities',
  community: 'Talk to other Crown & Rose authors in the live forum',
  bookshelf: 'View all your manuscripts and their publishability scores',
  editorial: 'Request a human editorial review of your manuscript',
  deviation: 'Review AI-flagged craft issues that need your attention',
};

export function ViewMenu({ visible, togglePanel }) {
  const { t } = useTranslation();
  const isGuidance = useIsGuidanceMode();
  return (
    <Menu label={t('menu.view')}>
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.panelsLabel')}
      </DropdownMenuLabel>
      {Object.keys(visible).map(k => (
        <DropdownMenuCheckboxItem
          key={k}
          checked={visible[k]}
          onCheckedChange={() => togglePanel(k)}
          className={`text-[12.5px] ${isGuidance && PANEL_TIPS[k] ? 'flex-col items-start gap-0 py-1.5' : ''}`}
        >
          <span>{t(`menu.panel_${k}`, k.charAt(0).toUpperCase() + k.slice(1))}</span>
          {isGuidance && PANEL_TIPS[k] && (
            <p className="mt-0.5 pl-[22px] text-[10.5px] leading-snug text-muted-foreground/75 font-normal">
              {PANEL_TIPS[k]}
            </p>
          )}
        </DropdownMenuCheckboxItem>
      ))}
      <DropdownMenuSeparator />
      <MenuItem onClick={() => toast.info(t('menu.resetWorkspaceToast'))}
        tip="Restore all panels to their default positions and sizes">
        {t('menu.resetWorkspaceLayout')}
      </MenuItem>
      <MenuItem tip="Hide all panels and banners for distraction-free writing">{t('menu.focusMode')}<DropdownMenuShortcut>F11</DropdownMenuShortcut></MenuItem>
    </Menu>
  );
}
